import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { formatCurrency, getCurrencySymbol } from '../utils/formatters';

const PAGE_SIZE = 10;

const DriverSalaryLogs = ({ user, operatorDetails }) => {
    const [logs, setLogs] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showImportScreen, setShowImportScreen] = useState(false);
    const [page, setPage] = useState(1);
    const [toast, setToast] = useState(null);
    const currencySymbol = getCurrencySymbol(operatorDetails?.country);
    
    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3000);
    };

    const [newLog, setNewLog] = useState({
        driverId: '',
        month: new Date().toISOString().slice(0, 7),
        baseSalary: 0,
        fixedAllowances: 0,
        overtimeAmount: 0,
        uniformDeduction: 0,
        accommodationDeduction: 0,
        otherDeduction: 0,
        status: 'Paid',
        remarks: ''
    });

    useEffect(() => {
        fetchDrivers();
        fetchLogs();
    }, [selectedMonth, user]);

    const fetchDrivers = async () => {
        try {
            const res = await fetch(`/api/drivers/${user.operatorId}`);
            const data = await res.json();
            // Filter only direct drivers for salary logs
            setDrivers(Array.isArray(data) ? data.filter(d => !d.vendorId) : []);
        } catch (err) {
            console.error("Error fetching drivers:", err);
        }
    };

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const res = await fetch(`/api/driver-salary-logs/${user.operatorId}?month=${selectedMonth}`);
            const data = await res.json();
            setLogs(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Error fetching logs:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleDriverChange = (driverId) => {
        const driver = drivers.find(d => d._id === driverId);
        if (driver) {
            setNewLog({
                ...newLog,
                driverId,
                baseSalary: driver.baseSalary || 0,
                fixedAllowances: driver.fixedAllowances || 0,
                uniformDeduction: driver.uniformCost || 0,
                accommodationDeduction: driver.accommodationCost || 0,
                otherDeduction: driver.otherDeduction || 0,
            });
        } else {
            setNewLog({ ...newLog, driverId: '' });
        }
    };

    const calculateTotal = (log) => {
        return (parseFloat(log.baseSalary) || 0) +
               (parseFloat(log.fixedAllowances) || 0) +
               (parseFloat(log.overtimeAmount) || 0) -
               (parseFloat(log.uniformDeduction) || 0) -
               (parseFloat(log.accommodationDeduction) || 0) -
               (parseFloat(log.otherDeduction) || 0);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const totalPayout = calculateTotal(newLog);
        try {
            const payload = { 
                ...newLog, 
                operatorId: user.operatorId, 
                totalPayout,
                paidAt: newLog.status === 'Paid' ? new Date() : null
            };
            const res = await fetch('/api/driver-salary-logs', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const text = await res.text();
            let data;
            try {
                data = JSON.parse(text);
            } catch (e) {
                showToast("Server returned invalid response", "error");
                return;
            }

            if (res.ok) {
                setShowCreateModal(false);
                fetchLogs();
                showToast("Salary log created successfully");
            } else {
                showToast(data.message || "Error creating log", "error");
            }
        } catch (err) {
            showToast("Network Error: " + err.message, "error");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this log?")) return;
        try {
            const res = await fetch(`/api/driver-salary-logs/${id}`, { method: 'DELETE' });
            if (res.ok) {
                showToast("Log deleted successfully");
                fetchLogs();
            } else {
                showToast("Failed to delete log", "error");
            }
        } catch (err) {
            showToast("Server error", "error");
        }
    };

    const handleStatusUpdate = async (log, status) => {
        try {
            const res = await fetch(`/api/driver-salary-logs/${log._id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ status, paidAt: status === 'Paid' ? new Date() : null })
            });
            if (res.ok) {
                showToast(`Status updated to ${status}`);
                fetchLogs();
            } else {
                showToast("Failed to update status", "error");
            }
        } catch (err) {
            showToast("Server error", "error");
        }
    };

    const stats = useMemo(() => {
        const totalPayout = logs.reduce((sum, log) => sum + (log.totalPayout || 0), 0);
        const paidCount = logs.filter(l => l.status === 'Paid').length;
        const pendingCount = logs.filter(l => l.status === 'Pending').length;
        return { totalPayout, paidCount, pendingCount, totalEntries: logs.length };
    }, [logs]);

    const totalPages = Math.ceil(logs.length / PAGE_SIZE);
    const paginatedLogs = logs.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const Icon = ({ d, size = 16, color = 'currentColor', strokeWidth = 2 }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round">
            {Array.isArray(d) ? d.map((path, i) => <path key={i} d={path} />) : <path d={d} />}
        </svg>
    );

    const icons = {
        plus: 'M12 5v14 M5 12h14',
        search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
        dollar: 'M12 1v22M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6',
        check: 'M20 6L9 17l-5-5',
        clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
        trash: 'M3 6h18 M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2 M10 11v6 M14 11v6',
        upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
    };

    if (showImportScreen) {
        return (
            <ImportWizard
                operatorId={user?.operatorId}
                onBack={() => setShowImportScreen(false)}
                onDone={() => { setShowImportScreen(false); fetchLogs(); }}
            />
        );
    }

    return (
        <div className="manage-drivers-page">
            <style>{`
                .manage-drivers-page { padding: 30px; background: #f8fafc; min-height: 100vh; font-family: 'Inter', sans-serif; }
                .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
                .title { font-size: 28px; font-weight: 800; color: #1e293b; margin: 0; }
                .subtitle { color: #64748b; font-size: 14px; margin-top: 4px; }
                
                .premium-btn {
                    display: flex; align-items: center; gap: 10px; padding: 12px 24px; border-radius: 14px;
                    font-weight: 700; font-size: 14px; cursor: pointer; transition: all 0.3s;
                    border: none; color: white; box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }
                .btn-primary { background: #11427D; }
                
                .stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 18px; margin-bottom: 24px; }
                .stat-card {
                    background: white; border-radius: 16px; padding: 20px; border: 1px solid #e2e8f0;
                    display: flex; align-items: center; gap: 16px; transition: all 0.25s;
                }
                .stat-icon-wrap {
                    width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
                }
                .stat-label { font-size: 11px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 2px; }
                .stat-val { font-size: 20px; font-weight: 800; color: #1e293b; }

                .toolbar { 
                    display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; 
                    background: white; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0;
                }
                .month-picker { padding: 10px; border-radius: 10px; border: 1px solid #e2e8f0; font-family: inherit; font-weight: 600; }

                .table-container { 
                    background: white; border-radius: 16px; overflow: hidden; border: 1px solid #e2e8f0;
                }
                .premium-table { width: 100%; border-collapse: collapse; }
                .premium-table th { 
                    background: #f8fafc; padding: 16px 20px; text-align: left; font-size: 12px; 
                    font-weight: 600; color: #64748b; text-transform: uppercase; border-bottom: 1px solid #e2e8f0;
                }
                .premium-table td { padding: 16px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; color: #334155; }
                
                .status-badge { padding: 4px 12px; border-radius: 99px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
                .status-paid { background: #ecfdf5; color: #059669; }
                .status-pending { background: #fffbeb; color: #d97706; }

                .op-modal-overlay {
                    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
                    background: rgba(15, 23, 42, 0.5); backdrop-filter: blur(4px);
                    display: flex; align-items: center; justify-content: center; z-index: 1000;
                }
                .op-modal-overlay { padding: 16px; }
                .op-modal {
                    background: white; border-radius: 20px; width: 100%; max-width: 600px;
                    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); overflow: hidden;
                    max-height: calc(100vh - 32px); display: flex; flex-direction: column;
                }
                .op-modal > form { display: flex; flex-direction: column; flex: 1; min-height: 0; }
                .modal-header { padding: 24px; border-bottom: 1px solid #f1f5f9; display: flex; justify-content: space-between; align-items: center; flex-shrink: 0; color: #0f172a; }
                .modal-body { padding: 24px; overflow-y: auto; flex: 1; min-height: 0; overscroll-behavior: contain; }
                .modal-footer { padding: 20px 24px; background: #f8fafc; display: flex; justify-content: flex-end; gap: 12px; flex-shrink: 0; border-top: 1px solid #f1f5f9; }
                @media (max-width: 560px) {
                    .op-modal .form-grid { grid-template-columns: 1fr; }
                    .op-modal .form-group { grid-column: auto !important; }
                    .modal-header, .modal-body { padding: 18px; }
                    .modal-footer { padding: 14px 18px; }
                }
                
                .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
                .form-group { display: flex; flex-direction: column; gap: 6px; }
                .form-group label { font-size: 12px; font-weight: 600; color: #64748b; }
                .form-input { padding: 10px; border: 1px solid #e2e8f0; border-radius: 8px; outline: none; }
                .form-input:focus { border-color: #00B2A9; }
                
                .payout-box { 
                    grid-column: span 2; background: #f1f5f9; padding: 16px; border-radius: 12px;
                    display: flex; justify-content: space-between; align-items: center; margin-top: 10px;
                }
                .toast {
                    position: fixed; top: 24px; right: 24px; padding: 14px 24px; border-radius: 10px;
                    background: #1e293b; color: white; z-index: 2000; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
                    animation: slideIn 0.3s ease-out; font-weight: 600;
                }
                .toast.success { background: #10b981; }
                .toast.error { background: #ef4444; }
                @keyframes slideIn { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
            `}</style>

            {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}

            <div className="header">
                <div>
                    <h1 className="title">Driver Salary Logs</h1>
                    <p className="subtitle">Track monthly payouts, allowances, and deductions for your fleet</p>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button className="premium-btn" style={{ background: '#10b981' }} onClick={() => setShowImportScreen(true)}>
                        <Icon d={icons.upload} size={18} strokeWidth={2.5} />
                        Import
                    </button>
                    <button className="premium-btn btn-primary" onClick={() => setShowCreateModal(true)}>
                        <Icon d={icons.plus} size={18} strokeWidth={2.5} />
                        Generate Salary Entry
                    </button>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card" style={{ borderLeft: '4px solid #11427D' }}>
                    <div className="stat-icon-wrap" style={{ background: '#eef2ff' }}>
                        <div style={{ fontSize: 24, fontWeight: 'bold', color: '#11427D' }}>{currencySymbol}</div>
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Total Expenditure ({selectedMonth})</span>
                        <span className="stat-val">{formatCurrency(stats.totalPayout, operatorDetails?.country)}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #10b981' }}>
                    <div className="stat-icon-wrap" style={{ background: '#ecfdf5' }}>
                        <Icon d={icons.check} color="#059669" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Paid Entries</span>
                        <span className="stat-val">{stats.paidCount}</span>
                    </div>
                </div>
                <div className="stat-card" style={{ borderLeft: '4px solid #f59e0b' }}>
                    <div className="stat-icon-wrap" style={{ background: '#fffbeb' }}>
                        <Icon d={icons.clock} color="#d97706" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Pending Payouts</span>
                        <span className="stat-val">{stats.pendingCount}</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon-wrap" style={{ background: '#f8fafc' }}>
                        <Icon d={icons.search} color="#64748b" size={24} />
                    </div>
                    <div className="stat-info">
                        <span className="stat-label">Total Records</span>
                        <span className="stat-val">{stats.totalEntries}</span>
                    </div>
                </div>
            </div>

            <div className="toolbar">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: '#64748b' }}>Select Period:</span>
                    <input 
                        type="month" 
                        className="month-picker"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                    />
                </div>
            </div>

            <div className="table-container">
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Driver</th>
                            <th>Month</th>
                            <th>Base Salary</th>
                            <th>Allowances</th>
                            <th>Overtime</th>
                            <th>Deductions</th>
                            <th>Total Payout</th>
                            <th>Status</th>
                            <th style={{ textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="9" style={{ textAlign: 'center', padding: 60 }}>Loading records...</td></tr>
                        ) : paginatedLogs.length === 0 ? (
                            <tr><td colSpan="9" style={{ textAlign: 'center', padding: 60 }}>No salary logs found for this period.</td></tr>
                        ) : paginatedLogs.map(log => {
                            const deductions = (log.uniformDeduction || 0) + (log.accommodationDeduction || 0) + (log.otherDeduction || 0);
                            return (
                                <tr key={log._id}>
                                    <td>
                                        <div style={{ fontWeight: 600, color: '#1e293b' }}>{log.driverId?.name}</div>
                                        <div style={{ fontSize: 12, color: '#64748b' }}>{log.driverId?.driverCode || 'N/A'}</div>
                                    </td>
                                    <td>{log.month}</td>
                                    <td>{formatCurrency(log.baseSalary, operatorDetails?.country)}</td>
                                    <td>{formatCurrency(log.fixedAllowances, operatorDetails?.country)}</td>
                                    <td>{formatCurrency(log.overtimeAmount, operatorDetails?.country)}</td>
                                    <td style={{ color: '#ef4444', fontWeight: 500 }}>-{formatCurrency(deductions, operatorDetails?.country)}</td>
                                    <td style={{ fontWeight: 800, color: '#0f172a', fontSize: 15 }}>{formatCurrency(log.totalPayout, operatorDetails?.country)}</td>
                                    <td>
                                        <button 
                                            className={`status-badge status-${log.status.toLowerCase()}`}
                                            style={{ border: 'none', cursor: 'pointer' }}
                                            onClick={() => handleStatusUpdate(log, log.status === 'Paid' ? 'Pending' : 'Paid')}
                                            title={`Click to mark as ${log.status === 'Paid' ? 'Pending' : 'Paid'}`}
                                        >
                                            {log.status}
                                        </button>
                                    </td>
                                    <td style={{ textAlign: 'right' }}>
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                            <button 
                                                style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: 4 }}
                                                onClick={() => handleDelete(log._id)}
                                                title="Delete Log"
                                            >
                                                <Icon d={icons.trash} size={16} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreateModal && (
                <div className="op-modal-overlay">
                    <div className="op-modal">
                        <div className="modal-header">
                            <h3 style={{ margin: 0, fontWeight: 800 }}>Generate Salary Entry</h3>
                            <button style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8' }} onClick={() => setShowCreateModal(false)}>&times;</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-grid">
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Select Driver</label>
                                        <select className="form-input" required value={newLog.driverId} onChange={(e) => handleDriverChange(e.target.value)}>
                                            <option value="">Choose a driver...</option>
                                            {drivers.map(d => (
                                                <option key={d._id} value={d._id}>{d.name} ({d.driverCode || 'N/A'})</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Month</label>
                                        <input type="month" className="form-input" required value={newLog.month} onChange={(e) => setNewLog({ ...newLog, month: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Base Salary</label>
                                        <input type="number" className="form-input" value={newLog.baseSalary} onChange={(e) => setNewLog({ ...newLog, baseSalary: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Fixed Allowances</label>
                                        <input type="number" className="form-input" value={newLog.fixedAllowances} onChange={(e) => setNewLog({ ...newLog, fixedAllowances: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Overtime Amount</label>
                                        <input type="number" className="form-input" value={newLog.overtimeAmount} onChange={(e) => setNewLog({ ...newLog, overtimeAmount: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Uniform Deduction</label>
                                        <input type="number" className="form-input" value={newLog.uniformDeduction} onChange={(e) => setNewLog({ ...newLog, uniformDeduction: e.target.value })} />
                                    </div>
                                    <div className="form-group">
                                        <label>Accommodation Cost</label>
                                        <input type="number" className="form-input" value={newLog.accommodationDeduction} onChange={(e) => setNewLog({ ...newLog, accommodationDeduction: e.target.value })} />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Other Deductions</label>
                                        <input type="number" className="form-input" value={newLog.otherDeduction} onChange={(e) => setNewLog({ ...newLog, otherDeduction: e.target.value })} />
                                    </div>
                                    <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                        <label>Payment Status</label>
                                        <select className="form-input" value={newLog.status} onChange={(e) => setNewLog({ ...newLog, status: e.target.value })}>
                                            <option value="Paid">Paid (Default)</option>
                                            <option value="Pending">Pending</option>
                                        </select>
                                    </div>
                                    
                                    <div className="payout-box">
                                        <span style={{ fontWeight: 600, color: '#64748b' }}>Estimated Net Payout</span>
                                        <span style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>
                                            {formatCurrency(calculateTotal(newLog), operatorDetails?.country)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="premium-btn" style={{ background: '#f1f5f9', color: '#64748b', boxShadow: 'none' }} onClick={() => setShowCreateModal(false)}>Cancel</button>
                                <button type="submit" className="premium-btn btn-primary">Save Salary Entry</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

function ImportWizard({ operatorId, onBack, onDone }) {
    const [file, setFile] = useState(null);
    const [dragActive, setDragActive] = useState(false);
    const [parsedData, setParsedData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [result, setResult] = useState(null);

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") {
            setDragActive(true);
        } else if (e.type === "dragleave") {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
    };

    const processFile = (selectedFile) => {
        setFile(selectedFile);
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: 'binary' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);

                const standardized = data.map((row) => {
                    const mapped = {};
                    Object.keys(row).forEach(key => {
                        const cleanKey = key.trim();
                        mapped[cleanKey] = row[key];
                    });
                    return mapped;
                });

                setParsedData(standardized);
            } catch (err) {
                console.error(err);
                alert("Failed to parse Excel file. Please ensure it is a valid .xlsx or .xls file.");
            }
        };
        reader.readAsBinaryString(selectedFile);
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                'Driver Code': 'D001',
                'Month (YYYY-MM)': '2026-05',
                'Base Salary': 3000,
                'Fixed Allowances': 500,
                'Overtime Amount': 150,
                'Uniform Deduction': 100,
                'Accommodation Deduction': 800,
                'Other Deduction': 50,
                'Total Payout': 2800,
                'Status': 'Paid',
                'Remarks': 'May salary'
            }
        ];
        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Salary Logs');
        XLSX.writeFile(wb, 'Salary_Logs_Template.xlsx');
    };

    const handleImport = async () => {
        if (parsedData.length === 0) return;
        setImporting(true);
        try {
            const mapped = parsedData.map(row => ({
                driverCode: row['Driver Code'],
                month: row['Month (YYYY-MM)'],
                baseSalary: row['Base Salary'] || 0,
                fixedAllowances: row['Fixed Allowances'] || 0,
                overtimeAmount: row['Overtime Amount'] || 0,
                uniformDeduction: row['Uniform Deduction'] || 0,
                accommodationDeduction: row['Accommodation Deduction'] || 0,
                otherDeduction: row['Other Deduction'] || 0,
                totalPayout: row['Total Payout'] || 0,
                status: row['Status'] || 'Paid',
                remarks: row['Remarks'] || ''
            }));

            const res = await fetch('/api/driver-salary-logs/import', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ operatorId, logs: mapped })
            });
            const data = await res.json();
            if (res.ok) {
                setResult({ createdCount: data.successCount, updatedCount: 0, errors: data.errors || [] });
            } else {
                alert(data.message || 'Import failed');
            }
        } catch (err) {
            console.error(err);
            alert("Import failed. Please try again.");
        } finally {
            setImporting(false);
        }
    };

    if (result) {
        return (
            <div className="manage-drivers-page" style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '40px', textAlign: 'center', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #f1f5f9' }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '80px', height: '80px', borderRadius: '50%', background: '#d1fae5', color: '#10b981', marginBottom: '24px' }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                    </div>
                    <h2 style={{ fontSize: '28px', fontWeight: '800', color: '#1e293b', marginBottom: '12px' }}>Import Completed!</h2>
                    <p style={{ fontSize: '15px', color: '#64748b', maxWidth: '600px', margin: '0 auto 30px' }}>
                        The salary logs data has been processed. Note that drivers were matched by their Driver Code.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', maxWidth: '500px', margin: '0 auto 30px' }}>
                        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#1e40af' }}>{result.createdCount || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#1d4ed8', marginTop: '4px' }}>Logs Created</div>
                        </div>
                        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '12px', padding: '20px', textAlign: 'center' }}>
                            <div style={{ fontSize: '32px', fontWeight: '800', color: '#92400e' }}>{(result.errors?.length) || 0}</div>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: '#b45309', marginTop: '4px' }}>Skipped / Warnings</div>
                        </div>
                    </div>
                    {result.errors && result.errors.length > 0 && (
                        <div style={{ textAlign: 'left', background: '#fef2f2', padding: '16px', borderRadius: '8px', marginBottom: '30px', maxHeight: '150px', overflowY: 'auto' }}>
                            <h4 style={{ margin: '0 0 8px', color: '#b91c1c', fontSize: '14px' }}>Errors:</h4>
                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#991b1b', fontSize: '13px' }}>
                                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        </div>
                    )}
                    <button onClick={onDone} style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '12px 30px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}>
                        Return to Logs
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="manage-drivers-page" style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <button onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '36px', height: '36px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
                    </button>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: 0 }}>Import Salary Logs</h1>
                        <p style={{ fontSize: '13px', color: '#64748b', margin: '4px 0 0' }}>Bulk import new salary entries using Driver Codes.</p>
                    </div>
                </div>
            </div>

            {parsedData.length === 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '400px 1fr', gap: '30px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>ℹ️ How it works</h3>
                            <ul style={{ paddingLeft: '20px', margin: 0, display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px', color: '#475569', lineHeight: '1.5' }}>
                                <li>The file must be an Excel sheet (<code>.xlsx</code> or <code>.xls</code>).</li>
                                <li><strong>Important:</strong> Drivers are matched exactly by their <strong>Driver Code</strong>. If a code isn't found, the row is skipped.</li>
                                <li>Month format should be <code>YYYY-MM</code> (e.g. <code>2026-05</code>).</li>
                            </ul>
                        </div>
                        <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ fontSize: '14px', fontWeight: '700', color: '#1e293b', marginBottom: '12px' }}>Supported Fields</h3>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                {['Driver Code', 'Month (YYYY-MM)', 'Base Salary', 'Fixed Allowances', 'Overtime Amount', 'Uniform Deduction', 'Accommodation Deduction', 'Other Deduction', 'Total Payout', 'Status', 'Remarks'].map((f, i) => (
                                    <span key={i} style={{ fontSize: '11px', fontWeight: '600', color: '#11427D', background: '#eef2ff', border: '1px solid #c3dcf5', padding: '4px 8px', borderRadius: '6px' }}>{f}</span>
                                ))}
                            </div>
                        </div>
                        <div style={{ background: '#f8fafc', borderRadius: '16px', border: '1px dashed #cbd5e1', padding: '20px', textAlign: 'center' }}>
                            <p style={{ fontSize: '13px', color: '#475569', margin: '0 0 12px' }}>Need a starting point? Download our pre-configured format template.</p>
                            <button onClick={downloadTemplate} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 16px', borderRadius: '8px', border: '1.5px solid #cbd5e1', background: 'white', color: '#334155', fontSize: '12.5px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                                Download Sample Template
                            </button>
                        </div>
                    </div>
                    <div
                        onDragEnter={handleDrag} onDragOver={handleDrag} onDragLeave={handleDrag} onDrop={handleDrop}
                        style={{
                            background: dragActive ? '#f0f7ff' : 'white', borderRadius: '16px', border: `2px dashed ${dragActive ? '#16539C' : '#cbd5e1'}`,
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 40px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', transition: 'all 0.15s', cursor: 'pointer'
                        }}
                        onClick={() => document.getElementById('file-upload-input').click()}
                    >
                        <input id="file-upload-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} />
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '16px', background: '#eef2ff', color: '#16539C', marginBottom: '20px' }}>
                            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
                        </div>
                        <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>Drag & Drop Excel File</h3>
                        <p style={{ fontSize: '13.5px', color: '#64748b', margin: '0 0 16px' }}>or click to browse your local files</p>
                        <span style={{ fontSize: '12px', color: '#94a3b8', background: '#f8fafc', padding: '6px 12px', borderRadius: '6px', border: '1px solid #f1f5f9' }}>Supports .xlsx, .xls formats</span>
                    </div>
                </div>
            ) : (
                <div style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <span style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>{file?.name}</span>
                                <span style={{ fontSize: '12px', color: '#64748b', background: '#cbd5e1', padding: '2px 8px', borderRadius: '999px', fontWeight: '600' }}>{parsedData.length} Rows Found</span>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button onClick={() => { setFile(null); setParsedData([]); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '10px', border: '1.5px solid #cbd5e1', background: 'white', color: '#475569', fontSize: '13px', fontWeight: '700', cursor: 'pointer', transition: 'all 0.15s' }}>Change File</button>
                            <button onClick={handleImport} disabled={importing} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 22px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#16539C,#00B2A9)', color: 'white', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(79,70,229,0.3)', transition: 'all 0.15s' }}>
                                {importing ? 'Processing...' : `Proceed Import (${parsedData.length})`}
                            </button>
                        </div>
                    </div>
                    <div style={{ overflowX: 'auto', maxHeight: '500px' }}>
                        <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: '800px' }}>
                            <thead>
                                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Driver Code</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Month</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Total Payout</th>
                                    <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '11px', fontWeight: '700', color: '#475569', textTransform: 'uppercase' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedData.map((d, i) => (
                                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                                        <td style={{ padding: '14px 20px', fontWeight: '700', color: '#1e293b' }}>{d['Driver Code'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d['Month (YYYY-MM)'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d['Total Payout'] || '—'}</td>
                                        <td style={{ padding: '14px 20px', color: '#475569', fontSize: '13px' }}>{d['Status'] || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DriverSalaryLogs;
